import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Lightning, Compass, Rocket, ShieldCheck, Sparkle, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import HeroMosaic from "@/components/HeroMosaic";
import InterestModal from "@/components/InterestModal";
import LiveSignalPill from "@/components/LiveSignalPill";
import IdeaLabPromo from "@/components/IdeaLabPromo";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const HERO_IMG = "https://images.unsplash.com/photo-1702538358013-61ed978425b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NjUyNzY5Nnww&ixlib=rb-4.1.0&q=85";

const PROMO_BG = "https://images.unsplash.com/photo-1664813953289-7c3350f040e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NjUyNzY5Nnww&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" } }),
};

export default function Home() {
  const [hero, setHero] = useState(null);
  const [howItWorks, setHowItWorks] = useState(null);
  const [demos, setDemos] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [promo, setPromo] = useState(null);
  const [interestOpen, setInterestOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { settings } = useSiteSettings();
  const [coIntroDismissed, setCoIntroDismissed] = useState(() => {
    try { return localStorage.getItem("appistry.co_innovation_banner_dismissed") === "1"; }
    catch { return false; }
  });
  const dismissCoIntroBanner = () => {
    try { localStorage.setItem("appistry.co_innovation_banner_dismissed", "1"); } catch {}
    setCoIntroDismissed(true);
  };
  const showCoIntroBanner = !!(settings.co_innovation_enabled && settings.co_innovation_just_enabled_banner) && !coIntroDismissed;

  useEffect(() => {
    const codeFromUrl = searchParams.get("promo");
    if (codeFromUrl) {
      try { localStorage.setItem("appistry.promo", codeFromUrl); } catch (_) {}
    }
  }, [searchParams]);

  // Decide whether the promo banner should render. Public if display_rule is "homepage"/"global";
  // gated when "code:X" — only shows when ?promo=X in URL or localStorage has it.
  const promoCodeUnlocked = (() => {
    if (!promo) return false;
    const rule = promo.display_rule || "homepage";
    if (!rule.startsWith("code:")) return true;
    const required = rule.split(":")[1];
    const fromUrl = searchParams.get("promo");
    let fromStorage = "";
    try { fromStorage = localStorage.getItem("appistry.promo") || ""; } catch (_) {}
    return fromUrl === required || fromStorage === required;
  })();

  useEffect(() => {
    api.get("/site-content").then(({ data }) => {
      setHero(data?.home_hero);
      setHowItWorks(data?.how_it_works);
    }).catch(() => {});
    // Fetch demos featured by the admin (max 3 shown on the homepage)
    api.get("/demos", { params: { featured: true } }).then(({ data }) => {
      setDemos((data || []).slice(0, 3));
    }).catch(() => {});
    api.get("/solutions").then(({ data }) => setSolutions(data.slice(0, 4))).catch(() => {});
    api.get("/promotions/active").then(({ data }) => setPromo(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* CO-INNOVATION REOPENED BANNER (admin-toggled fanfare) */}
      {showCoIntroBanner && (
        <motion.section
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative bg-gradient-to-r from-[hsl(var(--appistry-signal))] via-[#ff7849] to-[hsl(var(--appistry-teal))] text-white"
          data-testid="home-co-innovation-banner"
        >
          <div className="app-container py-3.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
            <Sparkle size={16} weight="fill" className="shrink-0" />
            <p className="text-sm font-semibold tracking-tight">
              <span className="font-mono uppercase tracking-widest text-[11px] mr-2 opacity-90">Just reopened</span>
              The Co-innovation programme is taking founding partners again.
            </p>
            <Link to="/co-innovation" className="text-sm font-semibold underline underline-offset-4 hover:opacity-90 inline-flex items-center gap-1" data-testid="home-co-innovation-banner-cta">
              See what's involved <ArrowRight size={12} weight="bold" />
            </Link>
            <button
              onClick={dismissCoIntroBanner}
              aria-label="Dismiss banner"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-1"
              data-testid="home-co-innovation-banner-dismiss"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </motion.section>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0 grid-dots opacity-40" />
        {/* Brand gradient glow — abstract, tech-native (no buildings) */}
        <div className="absolute right-[-10%] top-[-20%] h-[700px] w-[700px] rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--appistry-signal)) 0%, hsl(var(--appistry-teal)) 45%, transparent 70%)" }} />
        <div className="absolute right-[15%] bottom-[10%] h-[300px] w-[300px] rounded-full opacity-15 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--appistry-teal)) 0%, transparent 70%)" }} />
        <div className="app-container relative py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.p initial="hidden" animate="visible" variants={fadeUp} className="overline mb-6" data-testid="hero-overline">
              {hero?.overline || "Practical Apps, Thoughtfully Built"}
            </motion.p>
            <motion.h1
              initial="hidden"
              animate="visible"
              custom={1}
              variants={fadeUp}
              className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="hero-headline"
            >
              {hero?.headline || "Small, sharp apps that pay for themselves."}
            </motion.h1>
            <motion.p
              initial="hidden"
              animate="visible"
              custom={2}
              variants={fadeUp}
              className="text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              {hero?.subheadline ||
                "Appistry builds the unglamorous apps that quietly remove friction from your business — intake, inbox, scorecards, bookings. No bloat, no lock-in."}
            </motion.p>
            <motion.div initial="hidden" animate="visible" custom={3} variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to={hero?.primary_cta_href || "/contact"} className="btn-signal" data-testid="hero-primary-cta">
                {hero?.primary_cta_label || "See how we can help"}
                <ArrowRight size={18} weight="bold" />
              </Link>
              <button onClick={() => setInterestOpen(true)} className="btn-ghost-ink" data-testid="hero-interest-cta">
                Register interest
              </button>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              custom={3.5}
              variants={fadeUp}
              className="mt-6 text-sm text-zinc-500 max-w-xl"
              data-testid="hero-interest-note"
            >
              Not ready to chat yet? <button onClick={() => setInterestOpen(true)} className="underline underline-offset-4 hover:text-zinc-900 transition-colors">Drop your email</button> and we'll keep you posted as new concepts go live.
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              custom={4}
              variants={fadeUp}
              className="mt-16 grid grid-cols-3 max-w-lg"
            >
              {[
                { k: "Avg. first build", v: "14 days" },
                { k: "Partnership", v: "Long-term" },
                { k: "Deploys", v: "Same day" },
              ].map((s) => (
                <div key={s.k} className="border-l border-zinc-200 pl-4 first:border-l-0 first:pl-0">
                  <div className="overline text-zinc-500">{s.k}</div>
                  <div className="text-2xl font-bold tracking-tight mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.v}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* App mosaic — 6 tiles, Holborough tile live-ticking */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-5 hidden lg:block"
          >
            <HeroMosaic />
          </motion.div>
        </div>
      </section>

      {/* PROMOTION BANNER (admin-controlled) — takes priority over the IdeaLab banner */}
      {promo && promoCodeUnlocked ? (
        <section className="border-b border-zinc-200 bg-gradient-to-r from-[hsl(var(--appistry-signal))] to-[hsl(var(--appistry-teal))] text-white" data-testid="promo-banner">
          <div className="relative app-container py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 bg-white/15 border border-white/25 flex items-center justify-center">
                <Lightning size={20} weight="fill" color="white" />
              </div>
              <div>
                <div className="overline text-white/80">Active promotion</div>
                <div className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="promo-title">
                  {promo.title}
                </div>
                <div className="text-sm text-white/85 mt-0.5">{promo.description}</div>
              </div>
            </div>
            <Link to={`${promo.cta_href}?source=promotion`} className="inline-flex items-center gap-2 bg-white text-[hsl(var(--appistry-signal))] px-5 py-3 font-semibold hover:bg-zinc-50 transition-colors" data-testid="promo-cta">
              {promo.cta_label}
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </section>
      ) : (
        /* IdeaLab banner sits in the prime-real-estate slot whenever no active promo is showing */
        <IdeaLabPromo variant="banner" />
      )}

      {/* SOLUTIONS */}
      <section className="app-container py-24 md:py-32">
        <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-14">
          <div>
            <p className="overline mb-4">Problems we solve</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight max-w-2xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Real friction, named out loud.
            </h2>
          </div>
          <Link to="/solutions" className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="solutions-see-all">
            See all solutions <ArrowUpRight size={16} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-zinc-200">
          {solutions.map((s, i) => (
            <motion.div
              key={s.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeUp}
              custom={i}
              className="border-r border-b border-zinc-200 bg-white hover:bg-zinc-50 transition-colors group overflow-hidden"
              data-testid={`solution-card-${i}`}
            >
              {s.image && (
                <Link to={s.linked_demo_slug ? `/demos/${s.linked_demo_slug}` : "#"} className="block bg-zinc-100 overflow-hidden aspect-[16/9]">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    loading="eager"
                    fetchpriority={i < 2 ? "high" : "auto"}
                    data-testid={`solution-card-${i}-image`}
                  />
                </Link>
              )}
              <div className="p-8 md:p-10">
                <p className="overline text-zinc-500 mb-3">0{i + 1}</p>
                <h3 className="text-2xl font-semibold tracking-tight mb-3 flex items-start gap-2 flex-wrap" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <span>{s.title}</span>
                </h3>
                {s.badge && <div className="mb-3"><LiveSignalPill label={s.badge} testId={`solution-card-${i}-badge`} /></div>}
                <p className="text-zinc-600 mb-4">{s.problem}</p>
                <p className="text-sm text-zinc-500 mb-6">{s.summary}</p>
                {s.linked_demo_slug && (
                  <Link to={`/demos/${s.linked_demo_slug}`} className="text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all" data-testid={`solution-cta-${i}`}>
                    View the demo <ArrowRight size={14} weight="bold" />
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED DEMOS */}
      <section className="border-t border-zinc-200 bg-white">
        <div className="app-container py-24 md:py-32">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-14">
            <div>
              <p className="overline mb-4">Featured work</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                One live. Two concepts.
              </h2>
            </div>
            <Link to="/demos" className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="demos-see-all">
              See more <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {demos.slice(0, 3).map((d, i) => (
              <motion.div
                key={d.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                custom={i}
                className="border border-zinc-200 bg-white group hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                data-testid={`demo-card-${d.slug}`}
              >
                <Link to={`/demos/${d.slug}`}>
                  <div className="aspect-[4/3] bg-zinc-100 overflow-hidden border-b border-zinc-200">
                    {d.media_url && (
                      <img src={d.media_url} alt={d.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    )}
                  </div>
                  <div className="p-6">
                    <p className="overline text-zinc-500 mb-2">{d.category}</p>
                    <h3 className="text-xl font-semibold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{d.title}</h3>
                    <p className="text-sm text-zinc-600 leading-relaxed">{d.short_description}</p>
                    <div className="mt-4 text-sm font-semibold inline-flex items-center gap-1 text-[hsl(var(--appistry-signal))]">
                      Explore demo <ArrowRight size={14} weight="bold" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Link to="/guided-journey" className="btn-ghost-ink" data-testid="home-guided-journey-cta">
              <Compass size={18} weight="regular" /> Not sure which fits? Try the Concept Matcher.
            </Link>
          </div>
        </div>
      </section>

      {/* IDEALAB PROMO banner now sits below the hero. The featured-demos → how-it-works flow is clean. */}

      {/* HOW IT WORKS */}
      <section className="border-t border-zinc-200">
        <div className="app-container py-24 md:py-32">
          <p className="overline mb-4">How it works</p>          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight max-w-3xl mb-16" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Four steps. No theatre.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 border-t border-zinc-200">
            {(howItWorks?.steps || []).map((s, i) => (
              <div key={s.title} className="border-r border-b border-zinc-200 last:border-r-0 p-8 bg-white" data-testid={`how-it-works-step-${i}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="h-10 w-10 border border-zinc-300 flex items-center justify-center font-mono text-sm font-semibold">
                    0{i + 1}
                  </div>
                  {i === 0 && <Rocket size={20} weight="regular" className="text-zinc-400" />}
                  {i === 1 && <Lightning size={20} weight="regular" className="text-zinc-400" />}
                  {i === 2 && <Compass size={20} weight="regular" className="text-zinc-400" />}
                  {i === 3 && <ShieldCheck size={20} weight="regular" className="text-zinc-400" />}
                </div>
                <h3 className="text-xl font-semibold tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="app-container py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="overline text-zinc-500 mb-4">Next step</p>
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              One call. One shippable idea. No slide decks.
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <p className="text-zinc-400">Tell us where it hurts. We'll come back with sharper questions and the smallest next step that would actually help.</p>
            <Link to="/contact" className="btn-signal self-start" data-testid="home-final-cta">
              See how we can help <ArrowRight size={18} weight="bold" />
            </Link>
            <button onClick={() => setInterestOpen(true)} className="text-sm font-mono uppercase tracking-widest text-zinc-500 hover:text-white self-start mt-1 transition-colors" data-testid="home-final-interest">
              Or just register interest →
            </button>
          </div>
        </div>
      </section>

      <InterestModal
        open={interestOpen}
        onClose={() => setInterestOpen(false)}
        source="home_hero"
        title="Get on the list"
        body="Drop your email — we'll let you know as new concepts go live."
        ctaLabel="Register interest"
      />
    </div>
  );
}
